import {
  MeidenConnectCredentialsProvider,
  useMeidenConnectIdpTableGetUser,
  useMeidenConnectUser,
} from "@meiden-connect/auth-frontend-sdk";
import {
  broadcastTokenMessage,
  hasMeidenConnectAuthorizationCallbackParameters,
  hasPendingMeidenConnectTokenStoreReset,
  getTokenEndpointRateLimitedUntil,
  MeidenConnectAuthLoadingPane,
  MeidenConnectAuthorizationCallbackPane,
  MeidenConnectAuthRecoverySuccessMarker,
  MeidenConnectTokenStoreResetPane,
  setTokenEndpointRateLimitedUntil as rememberTokenEndpointRateLimitedUntil,
  subscribeTokenBroadcast,
} from "@osakabe/auth-service-provider";
import {
  Component,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { AuthenticatedApiRequestProvider, MockApiRequestProvider } from "./api-request-context";
import { AuthSessionContext, type AuthSessionContextValue } from "./auth-session-context";
import { MockCurrentActorProvider } from "./use-current-actor";
import {
  purgeAllMeidenConnectTokens,
  restoreSessionSnapshotFromMeidenConnect,
} from "./session-bootstrap";
import {
  clearSessionSnapshot,
  loadSessionSnapshot,
  saveSessionSnapshot,
  subscribeSessionSnapshot,
} from "./storage";
import type { SessionSnapshot } from "./types";
import { getWebConfig } from "../env";

interface Props {
  readonly children: ReactNode;
}

const MOCK_SESSION: SessionSnapshot = {
  userId: "mock-canvas-user",
  name: "Mock 利用者",
  source: "mock",
};

// MEIDEN CONNECT 認証 Shell。
// - authMode === "mock" のときは固定 SessionSnapshot で AuthSessionContext を提供する
// - authMode === "required" のときは以下の構成で動作する:
//   1. 起動時に localStorage の SessionSnapshot キャッシュ、続いて IndexedDB の MEIDEN CONNECT
//      トークンから復元を試み、復元できた場合はメイン UI を即時描画する。
//   2. SDK の `MeidenConnectCredentialsProvider` は `<Suspense fallback={null}>` と
//      `<SilentSubtreeErrorBoundary>` で囲まれた不可視サブツリーにのみマウントする。
//      トークン更新で SDK 内部が Suspense を発火しても、メイン UI が空白にならないようにする。
//   3. SessionSnapshot がローカルからも IndexedDB からも復元できなかった場合のみ、
//      SDK Provider を可視 fallback と一緒にマウントしてサインインへ誘導する。
export function AuthShell({ children }: Props): ReactNode {
  const config = getWebConfig();

  if (config.authMode === "mock") {
    return (
      <MockAuthShell>
        <MockCurrentActorProvider>
          <MockApiRequestProvider>{children}</MockApiRequestProvider>
        </MockCurrentActorProvider>
      </MockAuthShell>
    );
  }

  if (!config.authServiceOrigin || !config.serviceOrigin) {
    throw new Error(
      "authMode=required では authServiceOrigin と serviceOrigin の設定が必須です。/config/runtime.json を確認してください。"
    );
  }

  if (hasMeidenConnectAuthorizationCallbackParameters()) {
    // 同一タブで同じ state の /idp-cb に再着地し、かつ MEIDEN CONNECT 認証済みの
    // SessionSnapshot が既に残っている場合は、URL hash の `code` を SDK 内部が既に
    // 消費済みの可能性が高い (SDK 配下のクレデンシャルプロバイダーが裏で同じ
    // /api/oidc/token を叩いて成功させた後で利用者が手動リロードしたケース等)。
    // そのままペインを起動して `completeAuthorizationCallbackWithoutSdk` がもう一度
    // 同じ code を交換しに行くと、IdP が invalid_grant の連発を rate-limit として
    // 429 で拒否し、「アクセスが集中…」案内へ落ち続ける悪循環になる。この場合は
    // hash を捨ててアプリトップへ戻し、bootstrap 経路で IndexedDB の refresh token
    // から再開させる。
    //
    // 一方、以下の場合は通常通り callback を処理させる。
    // - 初回 /idp-cb 着地 (同 state を同タブで見たことがない) = 新規 code の正当な交換
    // - SessionSnapshot が無い / mock 由来 = 初回サインインフローや別アカウントログイン
    //
    // なお seen state の記録には sessionStorage を使っているため、別タブで先に同じ
    // /idp-cb#... を開いた直後にこのタブで同じ URL を開いた場合は cross-tab 共有が
    // 効かず、callback ペインが再起動する余地が残る。ただし利用者の主訴 (同一タブで
    // 1 時間後にエラー画面が出る) は SDK 内部の自動 refresh→再認可→リロードの流れ
    // なので、同一タブ抑止で実用上の主訴は解消する想定。
    const callbackState = readMeidenConnectCallbackState();
    const snapshot = loadSessionSnapshot();
    const isSameStateReentry =
      callbackState !== undefined && hasSeenMeidenConnectCallbackState(callbackState);
    const hasMeidenConnectSnapshot = snapshot?.source === "meiden-connect";
    if (isSameStateReentry && hasMeidenConnectSnapshot) {
      if (typeof window !== "undefined") {
        window.location.replace(new URL("/", window.location.origin).href);
      }
      return null;
    }
    if (callbackState !== undefined) {
      markMeidenConnectCallbackStateSeen(callbackState);
    }
    return (
      <MeidenConnectAuthorizationCallbackPane
        authServiceOrigin={config.authServiceOrigin}
        className="app-bootstrap"
        serviceOrigin={config.serviceOrigin}
      />
    );
  }

  if (hasPendingMeidenConnectTokenStoreReset()) {
    return (
      <MeidenConnectTokenStoreResetPane
        authServiceOrigin={config.authServiceOrigin}
        className="app-bootstrap"
        serviceOrigin={config.serviceOrigin}
      />
    );
  }

  return (
    <MeidenConnectAuthShell
      authServiceOrigin={config.authServiceOrigin}
      serviceOrigin={config.serviceOrigin}
    >
      <AuthenticatedApiRequestProvider>{children}</AuthenticatedApiRequestProvider>
    </MeidenConnectAuthShell>
  );
}

function MockAuthShell({ children }: { readonly children: ReactNode }): ReactNode {
  const signOut = useCallback(async () => {
    // mock 認証ではログイン状態を固定し、ローカル確認中に未ログイン状態を作らない。
  }, []);
  const value = useMemo<AuthSessionContextValue>(
    () => ({ user: MOCK_SESSION, isRestoring: false, signOut }),
    [signOut]
  );
  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>;
}

interface MeidenConnectAuthShellProps {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
  readonly children: ReactNode;
}

/**
 * MEIDEN CONNECT モード用の認証 Shell 本体。
 *
 * SessionSnapshot のキャッシュ管理と、SDK Provider のサブツリー隔離マウントを担当する。
 */
function MeidenConnectAuthShell({
  authServiceOrigin,
  serviceOrigin,
  children,
}: MeidenConnectAuthShellProps): ReactNode {
  // localStorage の SessionSnapshot で即時起動できるよう、初期値は localStorage から読み出す。
  const [session, setSession] = useState<SessionSnapshot | undefined>(() =>
    loadSessionSnapshot()
  );
  // 起動直後に IndexedDB から復元を試みている間は、未ログインとして扱わないように
  // restoring フラグを立てる。
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(
    () => loadSessionSnapshot() === undefined
  );
  // bootstrap で MEIDEN CONNECT IdP から 429 を受け取った場合に設定する。
  // この状態では SDK Provider の可視 fallback マウントを抑止し、専用の案内パネルへ
  // 切り替える。SDK Provider をそのままマウントすると IdP の連続失敗カウンタが
  // 更に押し上がり、ブロック時間が延長される恐れがある。
  const [bootstrapRateLimitState, setBootstrapRateLimitState] = useState<
    { readonly retryAfterSeconds: number | undefined } | undefined
  >(undefined);
  // MEIDEN CONNECT token endpoint が rate-limit 中の場合、SDK Provider を再マウントすると
  // 再認可遷移が先に走り、/idp-cb の rate-limit 画面へ戻されることがある。
  // localStorage / BroadcastChannel の共有状態を親 Shell でも監視し、解除までは SDK Provider
  // を止めて SessionSnapshot ベースの画面表示を維持する。
  const [tokenEndpointRateLimitedUntil, setTokenEndpointRateLimitedUntilState] = useState<
    number | undefined
  >(() => getTokenEndpointRateLimitedUntil());
  // SDK サブツリーがマウントされている間だけ非 undefined。SilentSubtreeErrorBoundary で
  // サブツリーが停止している場合は undefined のまま残り、親側でローカル状態のクリア +
  // IndexedDB token store の削除だけ行う best-effort 経路に切り替わる。
  // これにより SubTree 未マウント中のサインアウト要求が失われる問題を解消する。
  const sdkSignOutRef = useRef<(() => Promise<void>) | undefined>(undefined);
  // 復元中に別タブ同期 / signIn 等で session が変化した場合に、古い Snapshot で
  // 上書きしないよう、最新の session 値を ref で参照できるようにする。
  const sessionRef = useRef(session);
  // 別タブログアウト等で SessionSnapshot が `undefined` に戻った場合、`sessionRef.current` だけ
  // では「bootstrap 開始時点と同じ未ログイン状態」と区別できない。世代カウンタを別途持ち、
  // bootstrap 開始時と finally 時点で世代が一致しない場合は復元結果で巻き戻さない。
  const sessionSnapshotRevisionRef = useRef(0);
  // requestSignOut の二重発火を防ぐため、進行中の Promise を保持し完了するまで同じ Promise を返す。
  const signOutInFlightRef = useRef<Promise<void> | null>(null);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  // 別タブログインや待機後の復元で session が確立したら、429 案内の固定表示を解除する。
  useEffect(() => {
    if (session !== undefined && bootstrapRateLimitState !== undefined) {
      setBootstrapRateLimitState(undefined);
    }
  }, [session, bootstrapRateLimitState]);

  // bootstrap 経路で設定した 429 案内は、共有 rate-limit 状態の解除後に残さない。
  // 残ったままだと待機後も refresh 再試行へ進まず、案内画面で停止してしまう。
  useEffect(() => {
    if (tokenEndpointRateLimitedUntil === undefined && bootstrapRateLimitState !== undefined) {
      setBootstrapRateLimitState(undefined);
    }
  }, [bootstrapRateLimitState, tokenEndpointRateLimitedUntil]);

  // rate-limit 共有状態は他タブからも更新されるため、BroadcastChannel / storage 由来の通知、
  // タブ復帰、解除予定時刻のタイマーで読み直す。
  useEffect(() => {
    const refreshTokenEndpointRateLimit = (): void => {
      setTokenEndpointRateLimitedUntilState(getTokenEndpointRateLimitedUntil());
    };
    refreshTokenEndpointRateLimit();

    const unsubscribe = subscribeTokenBroadcast((message) => {
      if (
        message.type === "RATE_LIMITED" ||
        message.type === "RATE_LIMIT_CLEARED" ||
        message.type === "REFRESH_DONE"
      ) {
        refreshTokenEndpointRateLimit();
      }
    });

    window.addEventListener("focus", refreshTokenEndpointRateLimit);
    document.addEventListener("visibilitychange", refreshTokenEndpointRateLimit);

    return () => {
      unsubscribe();
      window.removeEventListener("focus", refreshTokenEndpointRateLimit);
      document.removeEventListener("visibilitychange", refreshTokenEndpointRateLimit);
    };
  }, []);

  useEffect(() => {
    if (tokenEndpointRateLimitedUntil === undefined) {
      return undefined;
    }
    const delayMs = Math.max(0, tokenEndpointRateLimitedUntil - Date.now()) + 250;
    const timeoutId = window.setTimeout(() => {
      setTokenEndpointRateLimitedUntilState(getTokenEndpointRateLimitedUntil());
    }, Math.min(delayMs, 2_147_483_647));

    return () => window.clearTimeout(timeoutId);
  }, [tokenEndpointRateLimitedUntil]);

  // 他タブで SessionSnapshot が変化した場合 (signOut / 別端末同期) に追従する。
  useEffect(() => {
    const unsubscribe = subscribeSessionSnapshot(() => {
      sessionSnapshotRevisionRef.current += 1;
      const latest = loadSessionSnapshot();
      // bootstrap finally 経路が同期的に参照できるよう sessionRef もここで更新する。
      sessionRef.current = latest;
      setSession((current) => (areSnapshotsEqual(current, latest) ? current : latest));
    });
    return unsubscribe;
  }, []);

  // 初回マウント時に SessionSnapshot が localStorage に無ければ、IndexedDB から復元を試みる。
  useEffect(() => {
    if (session !== undefined) {
      setIsBootstrapping(false);
      return undefined;
    }
    if (bootstrapRateLimitState !== undefined || tokenEndpointRateLimitedUntil !== undefined) {
      // rate-limit 中は refresh 自体を投げず、解除後に改めて bootstrap へ戻す。
      if (!isBootstrapping) {
        setIsBootstrapping(true);
      }
      return undefined;
    }

    let active = true;
    const startedSessionSnapshotRevision = sessionSnapshotRevisionRef.current;
    void restoreSessionSnapshotFromMeidenConnect({
      authServiceOrigin,
      serviceOrigin,
    })
      .then((result) => {
        if (!active) {
          return;
        }
        if (result.status === "rate-limited") {
          // IdP がレートリミット中。SDK Provider を可視マウントせずに、専用パネルで
          // 利用者へ待機を案内する経路へ切り替える。
          const rateLimitedUntil = rememberTokenEndpointRateLimitedUntil(
            result.retryAfterSeconds
          );
          setTokenEndpointRateLimitedUntilState(rateLimitedUntil);
          broadcastTokenMessage({
            type: "RATE_LIMITED",
            until: rateLimitedUntil,
            observedAt: Date.now(),
          });
          setBootstrapRateLimitState({ retryAfterSeconds: result.retryAfterSeconds });
          return;
        }
        if (result.status !== "session") {
          return;
        }
        // 復元中に他経路 (別タブ同期・初回サインイン bridge・別タブ signOut) で session 状態が
        // 変化していた場合は、古い Snapshot で新しい状態を巻き戻さないよう書き込みをスキップする。
        // `sessionRef.current !== undefined` は新規ログイン側、世代不一致は別タブ signOut 側を
        // それぞれ拾う。世代カウンタは `subscribeSessionSnapshot` 内で増えるため、
        // `clearSessionSnapshot` で `latest=undefined` になった場合も検出できる。
        if (
          sessionRef.current !== undefined ||
          sessionSnapshotRevisionRef.current !== startedSessionSnapshotRevision
        ) {
          return;
        }
        saveSessionSnapshot(result.snapshot);
        setSession(result.snapshot);
      })
      .finally(() => {
        if (active) {
          setIsBootstrapping(false);
        }
      });

    return () => {
      active = false;
    };
    // authServiceOrigin / serviceOrigin は親から渡される runtime config で実行中に変わらない。
  }, [
    authServiceOrigin,
    bootstrapRateLimitState,
    isBootstrapping,
    serviceOrigin,
    session,
    tokenEndpointRateLimitedUntil,
  ]);

  const executeSignOut = useCallback(async (): Promise<void> => {
    // SDK サブツリーが登録した signOut を最優先で呼ぶ。サブツリーが SilentSubtreeErrorBoundary
    // で停止していると ref は undefined のため、その場合は IndexedDB token store の削除と
    // ローカル状態のクリアだけ行う best-effort 経路に切り替わる。これにより SubTree 停止中で
    // ログアウトしても、後で SDK Provider が再マウントしたタイミングで自動再ログインしてしまう
    // 状態を防ぐ。
    const sdkSignOut = sdkSignOutRef.current;
    let signOutError: unknown;
    try {
      if (sdkSignOut !== undefined) {
        await sdkSignOut();
      } else {
        await purgeAllMeidenConnectTokens({
          authServiceOrigin,
          serviceOrigin,
        });
      }
    } catch (error) {
      signOutError = error;
      console.error("[auth] MEIDEN CONNECT サインアウトに失敗しました", error);
      // SDK signOut が失敗した場合、IndexedDB の token store にトークンが残ったままになり
      // 後で SDK Provider が再マウントしたタイミングで自動再ログインしてしまう恐れがある。
      // best-effort で IndexedDB token store を削除して、サインアウト要求を確実に反映させる。
      await purgeAllMeidenConnectTokens({ authServiceOrigin, serviceOrigin }).catch(
        () => undefined
      );
    }
    // 成功・失敗・サブツリー未マウントいずれの場合も localStorage / state は確実に片付ける。
    clearSessionSnapshot();
    setSession(undefined);
    if (signOutError !== undefined) {
      // 呼び出し元 (DashboardHeader) でエラー通知を出せるよう再投げする。
      throw signOutError;
    }
  }, [authServiceOrigin, serviceOrigin]);

  const requestSignOut = useCallback((): Promise<void> => {
    // ログアウトボタンの連打 / 完了前の二度押しで SDK の `user.signOut()` や
    // `purgeAllMeidenConnectTokens()` が並行実行されると、IndexedDB token store の削除順序や
    // IdP リダイレクトと reject の競合が起きうる。進行中の Promise を再利用して二重発火を防ぐ。
    const inFlight = signOutInFlightRef.current;
    if (inFlight !== null) {
      return inFlight;
    }
    const signOutPromise = executeSignOut().finally(() => {
      if (signOutInFlightRef.current === signOutPromise) {
        signOutInFlightRef.current = null;
      }
    });
    signOutInFlightRef.current = signOutPromise;
    return signOutPromise;
  }, [executeSignOut]);

  const contextValue = useMemo<AuthSessionContextValue>(
    () => ({
      user: session,
      isRestoring: isBootstrapping,
      signOut: requestSignOut,
    }),
    [session, isBootstrapping, requestSignOut]
  );

  // 既に bootstrap、別タブ、/idp-cb のいずれかで token endpoint の 429 が観測済みの場合は、
  // 可視 SDK Provider を起動せず待機案内に留める。これにより、TOP 復帰直後に再び /idp-cb
  // へ戻るループを防ぐ。
  if (session === undefined && tokenEndpointRateLimitedUntil !== undefined) {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <BootstrapRateLimitedPane
          retryAfterSeconds={readRemainingRateLimitSeconds(tokenEndpointRateLimitedUntil)}
        />
      </AuthSessionContext.Provider>
    );
  }

  // bootstrap が IdP のレートリミット (HTTP 429) を観測した場合は、SDK Provider の
  // 可視マウントを避けて「待機 + 再読込」だけを案内する。
  if (session === undefined && bootstrapRateLimitState !== undefined) {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <BootstrapRateLimitedPane
          retryAfterSeconds={bootstrapRateLimitState.retryAfterSeconds}
        />
      </AuthSessionContext.Provider>
    );
  }

  // セッション未取得かつ復元処理中: SDK Provider を可視マウントしない。
  // メイン UI 側は AuthSessionContext を介して user=undefined / isRestoring=true を観測できる。
  // 起動直後のごく短い時間に空白フォールバックが出ないよう、目立たないローディング表示にする。
  if (isBootstrapping && session === undefined) {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <MeidenConnectAuthLoadingPane
          authServiceOrigin={authServiceOrigin}
          className="app-bootstrap"
          serviceOrigin={serviceOrigin}
        />
      </AuthSessionContext.Provider>
    );
  }

  // セッション未取得かつ復元失敗: SDK Provider を可視マウントしてサインインを誘導する。
  // 通常運用では「初回サインイン」「リフレッシュトークン失効」のときだけここに来る。
  if (session === undefined) {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <Suspense
          fallback={
            <MeidenConnectAuthLoadingPane
              authServiceOrigin={authServiceOrigin}
              className="app-bootstrap"
              serviceOrigin={serviceOrigin}
            />
          }
        >
          <MeidenConnectCredentialsProvider
            authServiceOrigin={authServiceOrigin}
            origin={serviceOrigin}
          >
            <MeidenConnectAuthRecoverySuccessMarker />
            <MeidenConnectInitialSignInBridge
              onSnapshotChange={(next) => {
                saveSessionSnapshot(next);
                setSession(next);
              }}
              onSdkSignOutChange={(signOut) => {
                sdkSignOutRef.current = signOut;
              }}
            />
          </MeidenConnectCredentialsProvider>
        </Suspense>
      </AuthSessionContext.Provider>
    );
  }

  // セッション取得済み: メイン UI は SessionSnapshot キャッシュから即時描画。
  // SDK Provider は不可視サブツリーでプロフィール同期と signOut を担う。
  return (
    <AuthSessionContext.Provider value={contextValue}>
      {children}
      {session.source === "meiden-connect" &&
      tokenEndpointRateLimitedUntil === undefined ? (
        <SilentSubtreeErrorBoundary>
          <Suspense fallback={null}>
            <MeidenConnectCredentialsProvider
              authServiceOrigin={authServiceOrigin}
              origin={serviceOrigin}
            >
              <MeidenConnectAuthRecoverySuccessMarker />
              <MeidenConnectSessionSyncSubtree
                currentSession={session}
                onSnapshotChange={(next) => {
                  saveSessionSnapshot(next);
                  setSession(next);
                }}
                onSdkSignOutChange={(signOut) => {
                  sdkSignOutRef.current = signOut;
                }}
              />
            </MeidenConnectCredentialsProvider>
          </Suspense>
        </SilentSubtreeErrorBoundary>
      ) : null}
    </AuthSessionContext.Provider>
  );
}

function readRemainingRateLimitSeconds(rateLimitedUntil: number): number {
  return Math.max(1, Math.ceil((rateLimitedUntil - Date.now()) / 1_000));
}

interface MeidenConnectSessionSyncSubtreeProps {
  readonly currentSession: SessionSnapshot;
  readonly onSnapshotChange: (snapshot: SessionSnapshot) => void;
  /**
   * SDK の `user.signOut` を呼び出す関数を親へ ref 登録するコールバック。unmount 時は
   * `undefined` を渡して登録解除する。親側 (`requestSignOut`) は登録された関数を呼んで
   * サインアウトを実行する。サブツリーが SilentSubtreeErrorBoundary で停止している間は ref が
   * undefined のままなので、親側で IndexedDB token store 削除を含む best-effort 経路へ
   * フォールバックする。
   */
  readonly onSdkSignOutChange: (signOut: (() => Promise<void>) | undefined) => void;
}

/**
 * SDK Provider 配下で動作する「プロフィール同期 + signOut」処理本体。
 *
 * 自身は何も描画せず、SDK から得られた情報を親の AuthShell にコールバックで返す。
 * Suspense fallback=null + SilentSubtreeErrorBoundary 配下で動かすため、本サブツリーが
 * SDK 初期化で Suspense したり例外を投げたりしても、メイン UI には影響しない。
 */
function MeidenConnectSessionSyncSubtree({
  currentSession,
  onSnapshotChange,
  onSdkSignOutChange,
}: MeidenConnectSessionSyncSubtreeProps): ReactNode {
  const user = useMeidenConnectUser();
  const profileResult = useMeidenConnectIdpTableGetUser(user.userId);
  const avatarImageUrl = resolveSessionAvatarImageUrl({
    hasProfileRecord: profileResult.data !== undefined,
    profileAvatarImageUrl: profileResult.data?.avatarImageUrl,
    tokenAvatarImageUrl: user.avatarImageUrl,
  });
  const lastReportedSnapshotRef = useRef<SessionSnapshot | undefined>(currentSession);
  // SDK の signOut を親へ ref 経由で登録する。サブツリー unmount / SilentSubtreeErrorBoundary
  // による停止時は undefined を返して登録解除し、親側で best-effort 経路へフォールバックさせる。
  useEffect(() => {
    const signOut = (): Promise<void> => user.signOut();
    onSdkSignOutChange(signOut);
    return () => onSdkSignOutChange(undefined);
  }, [user, onSdkSignOutChange]);

  const snapshot = useMemo<SessionSnapshot>(
    () => ({
      userId: user.userId,
      name: user.name,
      email: user.email,
      affiliation: user.department,
      avatarImageUrl,
      source: "meiden-connect",
      globalPermissionIds: [...user.globalPermissionIds],
    }),
    [
      avatarImageUrl,
      user.department,
      user.email,
      user.globalPermissionIds,
      user.name,
      user.userId,
    ]
  );

  // SDK から得た最新の snapshot を親へ伝える。変化がなければ通知しない。
  useEffect(() => {
    if (areSnapshotsEqual(lastReportedSnapshotRef.current, snapshot)) {
      return;
    }
    lastReportedSnapshotRef.current = snapshot;
    onSnapshotChange(snapshot);
  }, [snapshot, onSnapshotChange]);

  return null;
}

interface MeidenConnectInitialSignInBridgeProps {
  readonly onSnapshotChange: (snapshot: SessionSnapshot) => void;
  /** SDK の `user.signOut` を親へ ref 経由で登録する。unmount 時は `undefined` を渡して解除する。 */
  readonly onSdkSignOutChange: (signOut: (() => Promise<void>) | undefined) => void;
}

/**
 * 初回サインイン経路。SessionSnapshot が一切無いときだけマウントされる。
 *
 * SDK Provider 配下で `useMeidenConnectUser` を読み、認可フローが完了して有効な
 * トークンが取れた時点で snapshot を親へ伝え、メイン UI を起動させる。
 */
function MeidenConnectInitialSignInBridge({
  onSnapshotChange,
  onSdkSignOutChange,
}: MeidenConnectInitialSignInBridgeProps): ReactNode {
  const user = useMeidenConnectUser();
  const profileResult = useMeidenConnectIdpTableGetUser(user.userId);
  const avatarImageUrl = resolveSessionAvatarImageUrl({
    hasProfileRecord: profileResult.data !== undefined,
    profileAvatarImageUrl: profileResult.data?.avatarImageUrl,
    tokenAvatarImageUrl: user.avatarImageUrl,
  });
  const lastReportedSnapshotRef = useRef<SessionSnapshot | undefined>(undefined);

  const snapshot = useMemo<SessionSnapshot>(
    () => ({
      userId: user.userId,
      name: user.name,
      email: user.email,
      affiliation: user.department,
      // 初回サインインでは idp-file: 解決完了を待たず、ID トークンの画像 URL をひとまず採用する。
      // サブツリー側の usePortableAvatarImageUrl は session 取得後にマウントされて再度走る。
      avatarImageUrl: isIdpFileUrl(avatarImageUrl) ? undefined : avatarImageUrl,
      source: "meiden-connect",
      globalPermissionIds: [...user.globalPermissionIds],
    }),
    [
      avatarImageUrl,
      user.department,
      user.email,
      user.globalPermissionIds,
      user.name,
      user.userId,
    ]
  );

  useEffect(() => {
    if (areSnapshotsEqual(lastReportedSnapshotRef.current, snapshot)) {
      return;
    }
    lastReportedSnapshotRef.current = snapshot;
    onSnapshotChange(snapshot);
  }, [snapshot, onSnapshotChange]);

  // 初回サインイン経路でも SDK signOut を親へ ref 登録しておく。SyncSubtree との実装一貫性のため。
  useEffect(() => {
    const signOut = (): Promise<void> => user.signOut();
    onSdkSignOutChange(signOut);
    return () => onSdkSignOutChange(undefined);
  }, [user, onSdkSignOutChange]);

  return null;
}

function isIdpFileUrl(value: string | undefined): value is string {
  return value?.startsWith("idp-file:") === true;
}

/**
 * bootstrap の refresh で MEIDEN CONNECT IdP から 429 を受け取った際の案内パネル。
 *
 * SDK Provider の可視 fallback を起動すると IdP 側の連続失敗カウンタが更に積み上がり
 * ブロック時間が延長されるため、ここでは「待機 + 再読込」のみを促す。
 */
function BootstrapRateLimitedPane({
  retryAfterSeconds,
}: {
  readonly retryAfterSeconds: number | undefined;
}): ReactNode {
  const handleReload = useCallback((): void => {
    window.location.reload();
  }, []);

  const waitGuidance = formatRateLimitWaitGuidance(retryAfterSeconds);

  return (
    <div className="app-bootstrap" role="alert" aria-live="polite">
      <div>
        <p>MEIDEN CONNECT 認証サーバーへのアクセスが集中しているため、認証応答を受け取れませんでした。</p>
        <p>{waitGuidance}</p>
        <p>業務データは削除されません。サインイン状態も維持されています。</p>
        <p>
          <button type="button" onClick={handleReload}>
            再読込する
          </button>
        </p>
      </div>
    </div>
  );
}

function formatRateLimitWaitGuidance(retryAfterSeconds: number | undefined): string {
  if (retryAfterSeconds === undefined) {
    return "しばらく時間を置いてから、もう一度アクセスしてください。";
  }
  if (retryAfterSeconds >= 60) {
    const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
    return `${minutes} 分ほど時間を置いてから、もう一度アクセスしてください。`;
  }
  return `${retryAfterSeconds} 秒ほど時間を置いてから、もう一度アクセスしてください。`;
}

export function resolveSessionAvatarImageUrlForTest(input: {
  readonly hasProfileRecord: boolean;
  readonly profileAvatarImageUrl: string | null | undefined;
  readonly tokenAvatarImageUrl: string | null | undefined;
}): string | undefined {
  return resolveSessionAvatarImageUrl(input);
}

function resolveSessionAvatarImageUrl(input: {
  readonly hasProfileRecord: boolean;
  readonly profileAvatarImageUrl: string | null | undefined;
  readonly tokenAvatarImageUrl: string | null | undefined;
}): string | undefined {
  // 認証復旧直後は ID トークン側の iconUrl が空になることがあるため、
  // IdP の User テーブルに最新プロフィールが取れている場合はそちらを正本にする。
  if (input.hasProfileRecord) {
    return normalizeAvatarImageUrl(input.profileAvatarImageUrl);
  }

  return normalizeAvatarImageUrl(input.tokenAvatarImageUrl);
}

function normalizeAvatarImageUrl(value: string | null | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

function areSnapshotsEqual(
  a: SessionSnapshot | undefined,
  b: SessionSnapshot | undefined
): boolean {
  if (a === b) {
    return true;
  }
  if (a === undefined || b === undefined) {
    return false;
  }
  return (
    a.userId === b.userId &&
    a.name === b.name &&
    a.email === b.email &&
    a.affiliation === b.affiliation &&
    a.avatarImageUrl === b.avatarImageUrl &&
    a.source === b.source &&
    areStringArraysEqual(a.globalPermissionIds, b.globalPermissionIds)
  );
}

function areStringArraysEqual(
  a: readonly string[] | undefined,
  b: readonly string[] | undefined
): boolean {
  if (a === b) {
    return true;
  }
  if (a === undefined || b === undefined) {
    return a === undefined && b === undefined;
  }
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}

/**
 * SDK 同期サブツリー専用のエラーバウンダリー。
 *
 * SDK 初期化や IndexedDB アクセスで投げられた例外がメイン UI に伝播しないよう静かに握りつぶす。
 * サブツリーが失敗してもキャッシュ済みの SessionSnapshot で UI を続行できる。
 * 一度エラー状態になっても、その後のタブ復帰 (visibility=visible) / focus / online
 * イベントで内部状態をリセットし、サブツリーを再マウントして同期を再開できるようにする。
 */
class SilentSubtreeErrorBoundary extends Component<
  { readonly children: ReactNode },
  { readonly hasError: boolean }
> {
  public override state: { readonly hasError: boolean } = { hasError: false };

  public static getDerivedStateFromError(): { readonly hasError: boolean } {
    return { hasError: true };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.warn(
      "[auth] バックグラウンド認証同期サブツリーで例外が発生したためサイレントに停止しました",
      error,
      errorInfo
    );
  }

  public override componentDidMount(): void {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }
    window.addEventListener("focus", this.handleRecoveryTrigger);
    window.addEventListener("online", this.handleRecoveryTrigger);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  }

  public override componentWillUnmount(): void {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }
    window.removeEventListener("focus", this.handleRecoveryTrigger);
    window.removeEventListener("online", this.handleRecoveryTrigger);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
  }

  /** エラー状態のときだけサブツリーを再マウントしてリトライする。健全時は no-op。 */
  private readonly handleRecoveryTrigger = (): void => {
    if (this.state.hasError) {
      this.setState({ hasError: false });
    }
  };

  private readonly handleVisibilityChange = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      this.handleRecoveryTrigger();
    }
  };

  public override render(): ReactNode {
    return this.state.hasError ? null : this.props.children;
  }
}

// MEIDEN CONNECT 認証コールバックの `state` パラメーターを sessionStorage 上で
// 「処理着手済み」マークするためのキー接頭辞。同タブで同じ state を二度見たかどうかを
// 判定する用途で、cross-tab で共有する必要はないため localStorage ではなく
// sessionStorage を使う。
const MEIDEN_CONNECT_CALLBACK_SEEN_STATE_KEY_PREFIX =
  "meiden-connect.auth.callback.seen-state.";

/** 現在の URL hash から MEIDEN CONNECT 認証コールバックの `state` を取り出す。 */
function readMeidenConnectCallbackState(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return new URLSearchParams(window.location.hash.slice(1)).get("state") ?? undefined;
}

/** sessionStorage に対象 `state` の「処理着手済み」マークが立っているかを返す。 */
function hasSeenMeidenConnectCallbackState(state: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return (
      window.sessionStorage.getItem(MEIDEN_CONNECT_CALLBACK_SEEN_STATE_KEY_PREFIX + state) === "1"
    );
  } catch {
    return false;
  }
}

/** sessionStorage に対象 `state` の「処理着手済み」マークを立てる。 */
function markMeidenConnectCallbackStateSeen(state: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(MEIDEN_CONNECT_CALLBACK_SEEN_STATE_KEY_PREFIX + state, "1");
  } catch {
    // sessionStorage が使えない環境では「同 state 再着地のスキップ」が効かなくなるが、
    // 元の問題 (IdP 429) は最終的に IdP 側の rate-limit cooldown で解消するため
    // 致命的ではない。例外を握りつぶしてフォールバック挙動 (= callback ペイン起動)
    // に任せる。
  }
}
